export const ACHLocationStatus = {
  UNVERIFIED: 'UNVERIFIED',
  RETRY: 'RETRY',
  DOCUMENT: 'DOCUMENT',
  VERIFIED: 'VERIFIED',
  SUSPENDED: 'SUSPENDED',
  KBA: 'KBA',
  DEACTIVATED: 'DEACTIVATED',
  PENDING_CERTIFY_BENEFICIAL_OWNERSHIP: 'PENDING_CERTIFY_BENEFICIAL_OWNERSHIP',
  BENEFICIAL_OWNERSHIP_DOCUMENTS_SUBMITTED:
    'BENEFICIAL_OWNERSHIP_DOCUMENTS_SUBMITTED',
  RETRY_VERIFICATION: 'RETRY_VERIFICATION',
  RETRY_WITH_FULL_SSN: 'RETRY_WITH_FULL_SSN',
  UNENROLLED: 'UNENROLLED',
  FUNDING_SOURCE_REQUIRED: 'FUNDING_SOURCE_REQUIRED',
} as const;

export const ACHFundingAccountStatus = {
  VERIFIED: 'VERIFIED',
  UNVERIFIED: 'UNVERIFIED',
} as const;

export const ACHLocationDocumentStatus = {
  REVIEWED: 'REVIEWED',
  VERIFIED: 'VERIFIED',
} as const;

export const ACHLocationDocument = {
  PASSPORT: 'PASSPORT',
  LICENSE: 'LICENSE',
} as const;

export const ACHLocationBusiness = {
  PERSONAL: 'PERSONAL',
  BUSINESS: 'BUSINESS',
} as const;

export const ACHBusinessType = {
  LLC: 'llc',
  PARTNERSHIP: 'partnership',
  CORPORATION: 'corporation',
  SOLE_PROPRIETORSHIP: 'soleProprietorship',
} as const;

export const BeneficialOwnerStatus = {
  UNVERIFIED: 'UNVERIFIED',
  DOCUMENT: 'DOCUMENT',
  VERIFIED: 'VERIFIED',
  INCOMPLETE: 'INCOMPLETE',
} as const;

export type ACHLocationStatusType =
  | (typeof ACHLocationStatus)[keyof typeof ACHLocationStatus];

export type ACHFundingAccountStatusType =
  (typeof ACHFundingAccountStatus)[keyof typeof ACHFundingAccountStatus];

export type ACHLocationDocumentStatusType =
  (typeof ACHLocationDocumentStatus)[keyof typeof ACHLocationDocumentStatus];

export type ACHLocationDocumentsTypeType =
  (typeof ACHLocationDocument)[keyof typeof ACHLocationDocument];

type ACHLocationBusinessType =
  (typeof ACHLocationBusiness)[keyof typeof ACHLocationBusiness];

export type ACHBusinessTypeType =
  (typeof ACHBusinessType)[keyof typeof ACHBusinessType];

export type BeneficialOwnerStatusType =
  (typeof BeneficialOwnerStatus)[keyof typeof BeneficialOwnerStatus];

export type ACHControllerType = {
  firstName: string;
  lastName: string;
  title: string;
  dateOfBirth: string;
  address: ACHAddress;
};

export type AchLocationType = {
  locationId: string;
  locationName: string;
  clientId: string;
  merchantId: string;
  externalId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  ipAddress: string;
  businessName: string;
  businessType: ACHBusinessTypeType;
  businessClassification: string;
  fundingAccount: ACHFundingAccountType;
  type: ACHLocationBusinessType;
  status: ACHLocationStatusType;
  acceptedTOSAndPP: boolean;
  achLocationDocuments: ACHLocationDocumentsType[];
  createdDate: string;
  modifiedDate: string;
  controller: ACHControllerType;
  copiedFromLocationId: string;
  sharedCustomer: boolean;
  sharedFundingSource: boolean;
  fundTransferEnabled: boolean;
  overallStatus: ACHLocationStatusType;
};

export type ACHLocationDocumentsType = {
  status: ACHLocationDocumentStatusType;
  type: string;
  failureReason: string;
};

export type AchLocationsType = AchLocationType[];

export type ACHFundingAccountType = {
  id: string;
  externalId: string;
  externalURL: string;
  bankAccountType: string;
  accountNickName: string;
  status: ACHFundingAccountStatusType;
  deleted: boolean;
  createdDate: string;
  modifiedDate: string;
  fingerprint: string;
};

export type ACHAddress = {
  address1: string;
  address2: string;
  address3: string;
  city: string;
  stateProvinceRegion: string;
  country: string;
  postalCode: string;
};

export type ACHTransfersConfigType = {
  clientId: string;
  frequency: 'MONTHLY';
  locations: {
    locationId: string;
    active: boolean;
  }[];
  createdDate: string;
  modifiedDate: string;
  active: boolean;
};

export type BeneficialOwnerDocumentType = {
  id: string;
  status: ACHLocationDocumentStatusType;
  type: string;
  created: string;
  failureReason: string | null;
  allFailureReasons: string[] | null;
};

export type BeneficialOwnersType = {
  beneficialOwners: {
    id: string;
    externalId: string;
    locationId: string;
    firstName: string;
    lastName: string;
    address: ACHAddress;
    createdDate: string;
    verificationStatus: BeneficialOwnerStatusType;
    documents: BeneficialOwnerDocumentType[];
  }[];
};

export const AuditEventName = {
  CREATED: 'CREATED',
  SUBMITTED_TO_DWOLLA: 'SUBMITTED_TO_DWOLLA',
  SUBMITTION_TO_DWOLLA_FAILED: 'SUBMITTION_TO_DWOLLA_FAILED',
  WEBHOOK_UPDATE: 'WEBHOOK_UPDATE',
  WEBHOOK_UPDATE_FAILED: 'WEBHOOK_UPDATE_FAILED',
  CANCELLATION_STARTED: 'CANCELLATION_STARTED',
  CANCELLED: 'CANCELLED',
  CANCELLATION_FAILED: 'CANCELLATION_FAILED',
  RETRY_TRANSFER_CREATED: 'RETRY_TRANSFER_CREATED',
} as const;

export type AuditEventNameType =
  (typeof AuditEventName)[keyof typeof AuditEventName];

export type AuditEvent = {
  id: string;
  eventName: AuditEventNameType;
  traceId: string;
  transferId: string;
  eventTime: string;
  interventionRequired: boolean;
  resolved: boolean;
  resolutionTime: string;
  resolutionComment: string;
  externalStatuses: Record<string, any>;
  additionalDetails: string;
};

export type ACHTransactionType = {
  id: string;
  externalId: string;
  parentTransferId: string;
  parentTransferExternalId: string;
  fundingTransferId: string;
  fundedTransferId: string;
  summaryId: string;
  clientId: string;
  merchantId: string;
  locationId: string;
  sourceLocationId: string;
  sourceLocationExternalId: string;
  sourceLocationName: string;
  destinationLocationId: string;
  destinationLocationExternalId: string;
  destinationLocationName: string;
  sourceFundingAccountId: string;
  sourceFundingAccountExternalId: string;
  sourceFundingAccountName: string;
  destinationFundingAccountId: string;
  destinationFundingAccountExternalId: string;
  destinationFundingAccountName: string;
  amount: number;
  amountFormatted: string;
  fullAmount: number;
  currencyCode: string;
  debitAccount: string;
  resolutionTime: string;
  resolutionComment: string;
  partialTransfer: boolean;
  status: string;
  links: Record<string, any>;
  failureReason: string;
  failureExceptions: unknown[];
  auditEvents: AuditEvent[];
  sourceAccountTransferStatusList: string[];
  destinationAccountTransferStatusList: string[];
  transferStartDate: string;
  transferEndDate: string;
  createdDate: string;
  modifiedDate: string;
};

export type ACHTransactionsType = {
  transferList: ACHTransactionType[];
  pageIndex: number;
  pageSize: number;
  total: number;
  totalRecordCount: number;
};
